import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { postRequest } from 'services';
import { Error } from 'components/shared';
import { Session } from 'redux/session/session.type';

const CODE_LENGTH = 6;

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .trim()
    .matches(/^\d{6}$/, 'Please enter the 6-digit code')
    .required('Verification code is required'),
});

const Lead = styled.p`
  font-size: 0.85rem;
  margin-bottom: 1.25rem;
  padding-top: 1rem;
  text-align: center;
`;

const FormArea = styled.div`
  max-width: 22.4375rem;
  margin: 0 auto;
`;

const Label = styled.label`
  display: block;
  text-align: left;
  font-size: 0.7rem;
  padding: 0.3125rem 0;
  margin-bottom: 0;
`;

const Boxes = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const Divider = styled.span`
  flex: none;
  width: 0.625rem;
  height: 0.09375rem;
  background-color: #ced4da;
`;

const Box = styled.input<{ $isInvalid?: boolean }>`
  width: 3.125rem;
  height: 3.75rem;
  padding: 0;
  text-align: center;
  font-size: 1.625rem !important;
  font-weight: 700;
  color: #212529;
  background-color: #fff;
  border: 0.09375rem solid ${(props) => (props.$isInvalid ? '#dc3545' : '#ced4da')};
  border-radius: 0.75rem;
  outline: none;
  caret-color: #007bff;
  transition: border-color 0.16s ease-out, box-shadow 0.16s ease-out;

  &:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.1875rem rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    background-color: #e9ecef;
  }
`;

const FieldError = styled.div`
  text-align: left;
  font-size: 0.8rem;
  color: #dc3545;
  padding-top: 0.375rem;
`;

const SubmitButton = styled.button`
  display: block;
  width: 100%;
  margin: 0.625rem auto;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
  color: #fff;
  background-color: #007bff;
  border: 0.0625rem solid #007bff;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;

  &:hover {
    background-color: #0069d9;
    border-color: #0062cc;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(38, 143, 255, 0.5);
  }

  &:disabled {
    opacity: 0.65;
    cursor: default;
  }
`;

const BackToLogin = styled.button`
  display: inline-block;
  background: none;
  border: none;
  padding: 0;
  font-size: 1rem;
  color: #007bff;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: #0056b3;
    text-decoration: underline;
  }
`;

interface MfaFormValues {
  code: string;
}

interface MfaCodeFormProps {
  email?: string;
  onLoginSuccess: (session: Session) => void;
  onBack: () => void;
}

/**
 * Displayed right after a successful login when the API reports that
 * multi factor authentication is still pending (`mfaPending: true`).
 * The user is asked to enter the one-time code that was sent to their e-mail.
 *
 * The code is entered into six single-digit boxes: typing advances to the next
 * box, backspace steps back, pasting distributes the digits, and the form
 * submits itself as soon as all six digits are present.
 */
function MfaCodeForm({ email, onLoginSuccess, onBack }: MfaCodeFormProps) {
  const [errorMsg, setErrorMessage] = useState('');
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const focusBox = (index: number) => {
    const input = inputRefs.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  };

  // Writes the digits into state, keeps Formik's `code` in sync and
  // submits once every box is filled.
  const commit = (
    next: string[],
    setFieldValue: FormikProps<MfaFormValues>['setFieldValue'],
    submitForm: FormikProps<MfaFormValues>['submitForm'],
    isSubmitting: boolean
  ) => {
    setDigits(next);
    const code = next.join('');
    setFieldValue('code', code);

    if (code.length === CODE_LENGTH && !isSubmitting) {
      // Let Formik pick up the new value before validating.
      setTimeout(submitForm, 0);
    }
  };

  const handleChange = (index: number, rawValue: string, formikProps: FormikProps<MfaFormValues>) => {
    const { setFieldValue, submitForm, isSubmitting } = formikProps;
    const typed = rawValue.replace(/\D/g, '');

    // A whole code pasted (or autofilled) into one box.
    if (typed.length > 1) {
      const next = Array(CODE_LENGTH).fill('');
      typed
        .slice(0, CODE_LENGTH)
        .split('')
        .forEach((digit, i) => {
          next[i] = digit;
        });
      commit(next, setFieldValue, submitForm, isSubmitting);
      focusBox(Math.min(typed.length, CODE_LENGTH) - 1);
      return;
    }

    const next = [...digits];
    next[index] = typed.slice(-1);
    commit(next, setFieldValue, submitForm, isSubmitting);

    if (next[index] && index < CODE_LENGTH - 1) focusBox(index + 1);
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>, formikProps: FormikProps<MfaFormValues>) => {
    const { setFieldValue, submitForm, isSubmitting } = formikProps;

    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      commit(next, setFieldValue, submitForm, isSubmitting);
      focusBox(index - 1);
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>, formikProps: FormikProps<MfaFormValues>) => {
    const { setFieldValue, submitForm, isSubmitting } = formikProps;
    const pasted = (event.clipboardData.getData('text') || '').replace(/\D/g, '');
    if (!pasted) return;

    event.preventDefault();
    const next = Array(CODE_LENGTH).fill('');
    pasted
      .slice(0, CODE_LENGTH)
      .split('')
      .forEach((digit, i) => {
        next[i] = digit;
      });
    commit(next, setFieldValue, submitForm, isSubmitting);
    focusBox(Math.min(pasted.length, CODE_LENGTH) - 1);
  };

  const handleSubmit = async (code: string, setSubmitting: (submitting: boolean) => void) => {
    setErrorMessage('');

    try {
      const response = await postRequest('/session/mfa/code', { code });
      setSubmitting(false);
      onLoginSuccess(response.data);
    } catch (error: any) {
      setSubmitting(false);
      setErrorMessage(error?.response?.data?.error || 'Invalid or expired verification code. Please try again.');
    }
  };

  const handleLogout = async () => {
    setErrorMessage('');

    try {
      await postRequest('/session/logout', {});
    } finally {
      onBack();
    }
  };

  return (
    <div>
      <Lead>
        We&apos;ve sent a 6-digit verification code to {email ? <strong>{email}</strong> : 'your e-mail address'}
        <br />
        Enter it below to finish signing in.
      </Lead>

      <Formik<MfaFormValues>
        initialValues={{ code: '' }}
        validationSchema={validationSchema}
        onSubmit={({ code }, { setSubmitting }) => handleSubmit(code.trim(), setSubmitting)}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(formikProps) => {
          const { values, errors, isSubmitting, submitForm } = formikProps;

          return (
            <FormArea>
              <Label htmlFor="mfa-code-0">Verification code</Label>

              <Boxes onPaste={(event) => handlePaste(event, formikProps)}>
                {digits.map((digit, index) => (
                  // eslint-disable-next-line react/no-array-index-key -- fixed-length, position-stable array of digit boxes
                  <React.Fragment key={index}>
                    {index === CODE_LENGTH / 2 && <Divider />}
                    <Box
                      id={`mfa-code-${index}`}
                      ref={(element: HTMLInputElement | null) => {
                        inputRefs.current[index] = element;
                      }}
                      $isInvalid={!!errors.code}
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      maxLength={CODE_LENGTH}
                      aria-label={`Digit ${index + 1}`}
                      value={digit}
                      disabled={isSubmitting}
                      onChange={(event) => handleChange(index, event.target.value, formikProps)}
                      onKeyDown={(event) => handleKeyDown(index, event, formikProps)}
                      onFocus={(event) => event.target.select()}
                    />
                  </React.Fragment>
                ))}
              </Boxes>

              {typeof errors.code === 'string' && <FieldError>{errors.code}</FieldError>}

              <SubmitButton
                type="button"
                disabled={isSubmitting || values.code.length !== CODE_LENGTH}
                onClick={() => submitForm()}
              >
                Verify code
              </SubmitButton>
            </FormArea>
          );
        }}
      </Formik>

      <div style={{ textAlign: 'center' }}>
        <BackToLogin
          type="button"
          onClick={(event) => {
            event.preventDefault();
            handleLogout();
          }}
        >
          Back to login
        </BackToLogin>
        <Error shown={!!errorMsg} style={{ paddingTop: '0.5rem' }}>{errorMsg}</Error>
      </div>
    </div>
  );
}

export default MfaCodeForm;
